import { motion, Variants } from 'framer-motion';
import React, { ReactNode, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { TDR } from 'tdr-common';
import { ModalContainer } from '../components/__design-system/modal/modal-container';
import { AnimatedImageGallery } from '../components/animated-image-gallery/AnimatedImageGallery';
import ScrollToHashElement from '../components/scroll-to-hash-element/ScrollToHashElement';
import { useMatterport } from '../context/new/MatterportContext';
import { useViewToggle } from '../context/ViewToggleProvider';
import { useScreenSize } from '../hooks/useScreenSize';
import 'react-toastify/dist/ReactToastify.css';
import { DesktopHeader } from './DesktopHeader';

export interface ModificationLayoutProps {
	restaurant: TDR.Restaurant;
	tables: TDR.Table[];
	children: ReactNode;
}

export function ModificationLayout({ restaurant, tables, children }: ModificationLayoutProps) {
	const { isError: matterportError } = useMatterport();

	const params = useParams();
	const { isSmallScreen } = useScreenSize();
	const { isVirtualView, turnOffVirtualView } = useViewToggle();

	useEffect(() => {
		if (!isSmallScreen || matterportError) {
			turnOffVirtualView();
		}
	}, [isSmallScreen, matterportError]);

	const sidebarVariants: Variants = {
		hidden: {
			opacity: 0,
			visibility: 'hidden',
			transition: {
				duration: 0.6,
				// only add delay on table details route
				delay: params?.tableSlug ? 0.5 : 0
			}
		},
		visible: {
			opacity: 1,
			visibility: 'visible',
			transition: {
				when: 'beforeChildren',
				duration: 0.7
			}
		}
	};
	return (
		<main className="h-full sm:flex sm:flex-col sm:gap-3 sm:p-3">
			<div className='hidden sm:block'>
				<DesktopHeader restaurantProp={restaurant} />
			</div>
			<div className={`relative flex gap-3 flex-1 h-full sm:h-auto sm:flex-1 sm:min-h-0 min-h-0 ${isVirtualView ? 'overflow-hidden' : ''}`}>
				<ScrollToHashElement />

				<motion.section
					id='sidebar-container'
					className={`w-full sm:w-[400px] sm:rounded-2xl sm:border-[1.5px] sm:border-tablz-med-gray-4 sm:relative sm:overflow-auto ${isVirtualView && isSmallScreen ? 'absolute inset-0' : ''} z-10`}
					initial={'visible'}
					variants={sidebarVariants}
					animate={isVirtualView ? 'hidden' : 'visible'}
				>
					{children}
					<ToastContainer position='bottom-left' theme='dark' />
					<ModalContainer />
				</motion.section>

				<section
					className="hidden sm:block h-full w-full overflow-hidden rounded-none sm:h-auto sm:rounded-2xl sm:border-[1.5px] sm:border-tablz-med-gray-4 sm:relative sm:overflow-hidden m-0 sm:flex-1"
				>
					<AnimatedImageGallery staticImage={true} restaurant={restaurant} tables={tables} />
				</section>
			</div>

		</main>
	);
}
